import { useCallback, useEffect, useRef, useState } from 'react';

import { WITHDRAWAL_ENABLED } from '@/lib/const';
import { fetchGuestWithdrawalEligibility } from '@/lib/withdrawal/guestWithdrawalEligibilityApi';
import type { WithdrawalEligibility } from '@/lib/withdrawal/types';
import type { OrderDetailsGuestSession } from '@/views/OrderDetailsView/helpers';

export function useGuestWithdrawalEligibility(
  guestSession: OrderDetailsGuestSession | undefined,
  enabled = true,
) {
  const [guestEligibilityCaptchaKey, setGuestEligibilityCaptchaKey] = useState(
    Date.now(),
  );
  const [guestEligibilityRecaptchaToken, setGuestEligibilityRecaptchaToken] =
    useState<string | null>(null);
  const [guestEligibilityLocal, setGuestEligibilityLocal] =
    useState<WithdrawalEligibility | null>(null);
  const [guestHasCheckedEligibility, setGuestHasCheckedEligibility] =
    useState(false);
  const [guestEligibilityFetchFailed, setGuestEligibilityFetchFailed] =
    useState(false);
  const lastFetchedTokenRef = useRef<string | null>(null);
  const onEligibilityChangeRef = useRef(guestSession?.onEligibilityChange);

  useEffect(() => {
    onEligibilityChangeRef.current = guestSession?.onEligibilityChange;
  });

  useEffect(() => {
    if (!WITHDRAWAL_ENABLED || !enabled) {
      return;
    }

    let cancelled = false;

    async function loadGuestEligibility() {
      if (!guestSession?.guestLookup || !guestEligibilityRecaptchaToken) {
        return;
      }

      if (lastFetchedTokenRef.current === guestEligibilityRecaptchaToken) {
        return;
      }
      lastFetchedTokenRef.current = guestEligibilityRecaptchaToken;

      const nextEligibility = await fetchGuestWithdrawalEligibility({
        businessId: guestSession.guestLookup.businessId,
        email: guestSession.guestLookup.email,
        recaptchaToken: guestEligibilityRecaptchaToken,
      });

      if (cancelled) return;

      if (nextEligibility === null) {
        setGuestEligibilityFetchFailed(true);
        setGuestHasCheckedEligibility(true);
        setGuestEligibilityLocal(null);
        setGuestEligibilityRecaptchaToken(null);
        setGuestEligibilityCaptchaKey((k) => k + 1);
        lastFetchedTokenRef.current = null;
        return;
      }

      setGuestEligibilityFetchFailed(false);
      setGuestEligibilityLocal(nextEligibility);
      setGuestHasCheckedEligibility(true);
      onEligibilityChangeRef.current?.(nextEligibility);
    }

    loadGuestEligibility();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    guestEligibilityRecaptchaToken,
    guestSession?.guestLookup?.businessId,
    guestSession?.guestLookup?.email,
  ]);

  const handleRecaptchaChange = useCallback((token: string | null) => {
    setGuestEligibilityRecaptchaToken(token);
    if (token) {
      setGuestEligibilityFetchFailed(false);
    }
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setGuestEligibilityRecaptchaToken(null);
    setGuestEligibilityCaptchaKey(Date.now());
  }, []);

  return {
    guestEligibilityCaptchaKey,
    guestEligibilityFetchFailed,
    guestEligibilityLocal,
    guestEligibilityRecaptchaToken,
    guestHasCheckedEligibility,
    handleRecaptchaChange,
    handleRecaptchaExpired,
  };
}
